import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, MessageCircle } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { DynamicIcon } from '@/utils/iconMap';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CoursesPage = () => {
  const { settings } = useSiteSettings();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/courses-content`);
        setContent(res.data);
      } catch (error) {
        console.error('Failed to fetch courses content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const whatsapp = settings?.whatsapp || '85295858258';
  const heroTitle = content?.hero_title || '課程資訊';
  const heroSubtitle = content?.hero_subtitle || '選擇最適合您的課程，開啟學車之旅';
  const courses = content?.courses || [];
  const sectionFeatures = content?.section_features || [];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 tracking-tight">
            {heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">載入中...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {courses.map((course) => (
                  <Card
                    key={course.id}
                    className={`relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white ${
                      course.is_featured ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <div className={`h-1.5 ${course.is_featured ? 'bg-gradient-to-r from-blue-500 to-yellow-500' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`} />
                    {course.badge && (
                      <div className="absolute top-5 right-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                        {course.badge}
                      </div>
                    )}
                    <CardHeader className="text-center pb-2 pt-8">
                      <CardTitle className="text-2xl mb-1">{course.title}</CardTitle>
                      {course.subtitle && (
                        <p className="text-sm text-muted-foreground">{course.subtitle}</p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        {(course.features || []).map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        asChild
                        className="w-full rounded-full bg-green-500 hover:bg-green-600 text-white"
                        size="lg"
                      >
                        <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp 查詢
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {sectionFeatures.length > 0 && (
                <div className="mt-16 bg-blue-50 rounded-2xl p-8 md:p-12">
                  <h2 className="text-3xl font-bold mb-8 text-center">課程特色</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {sectionFeatures.map((feature) => (
                      <div key={feature.id} className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <DynamicIcon name={feature.icon} className="h-6 w-6 text-primary" />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-2">{feature.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoursesPage;
